import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    TIERS,
    TIER_LICENSE_BOUNDS,
    PRICING_CURRENCY,
    getTierById as staticGetTierById,
    getTierLicenseBounds as staticGetTierLicenseBounds,
    formatMoney,
} from "../data/pricingContent";
import { fetchPricingPlans } from "../lib/pricingPlansApi";

const PricingPlansContext = createContext(null);

let pricingPlansPromise = null;

function loadPricingPlans() {
    if (!pricingPlansPromise) {
        pricingPlansPromise = fetchPricingPlans().catch((err) => {
            pricingPlansPromise = null;
            throw err;
        });
    }
    return pricingPlansPromise;
}

function mapApiTier(tier) {
    return {
        id: tier.id,
        name: tier.name,
        audience: tier.audience ?? "",
        licenses: tier.licenses ?? "",
        monthly: tier.monthly,
        yearly: tier.yearly,
        cta: tier.cta ?? "Purchase Now",
        primary: Boolean(tier.primary),
        isEnterprise: Boolean(tier.is_enterprise),
        highlights: Array.isArray(tier.highlights) ? tier.highlights : [],
    };
}

export function PricingPlansProvider({ children }) {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        loadPricingPlans()
            .then((data) => {
                if (!cancelled) {
                    setApiData(data);
                    setError("");
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setApiData(null);
                    setError(err.message ?? "Failed to load pricing");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const tiers = useMemo(() => {
        if (apiData?.tiers?.length) {
            return apiData.tiers.map(mapApiTier);
        }
        return TIERS;
    }, [apiData]);

    const currency = apiData?.currency ?? PRICING_CURRENCY;

    const licenseBounds = useMemo(() => {
        if (apiData?.license_bounds) {
            return apiData.license_bounds;
        }
        return TIER_LICENSE_BOUNDS;
    }, [apiData]);

    const getTierById = useCallback(
        (tierId) =>
            tiers.find((t) => t.id === tierId) ??
            tiers[0] ??
            staticGetTierById(tierId),
        [tiers],
    );

    const getTierLicenseBounds = useCallback(
        (tierId) =>
            licenseBounds[tierId] ??
            licenseBounds[tierId?.toLowerCase?.()] ??
            staticGetTierLicenseBounds(tierId),
        [licenseBounds],
    );

    const clampLicenseCount = useCallback(
        (tierId, count) => {
            const bounds = getTierLicenseBounds(tierId);
            const n = Number(count);
            if (!Number.isFinite(n)) return bounds.min;
            const max = bounds.max ?? n;
            return Math.min(Math.max(Math.round(n), bounds.min), max);
        },
        [getTierLicenseBounds],
    );

    const calculatePricingTotal = useCallback(
        (tierId, licenseCount, yearly) => {
            const tier = getTierById(tierId);
            if (!tier || tier.monthly === null) return null;

            const licenseCountClamped = clampLicenseCount(tierId, licenseCount);
            const perLicense = yearly ? tier.yearly : tier.monthly;
            const amount = licenseCountClamped * perLicense;

            return {
                licenseCount: licenseCountClamped,
                perLicense,
                perLicenseMonthlyDisplay: yearly
                    ? Math.round(tier.yearly / 12)
                    : tier.monthly,
                amount,
                currency,
                billingPeriod: yearly ? "year" : "month",
                label: yearly ? "Annual" : "Monthly",
            };
        },
        [clampLicenseCount, currency, getTierById],
    );

    const formatPrice = useCallback(
        (amount) => formatMoney(amount, currency),
        [currency],
    );

    const isEnterpriseTier = useCallback(
        (tier) => Boolean(tier?.isEnterprise) || tier?.monthly === null,
        [],
    );

    const value = useMemo(
        () => ({
            tiers,
            currency,
            licenseBounds,
            loading,
            error,
            fromApi: Boolean(apiData?.tiers?.length),
            getTierById,
            getTierLicenseBounds,
            clampLicenseCount,
            calculatePricingTotal,
            formatPrice,
            isEnterpriseTier,
        }),
        [
            tiers,
            currency,
            licenseBounds,
            loading,
            error,
            apiData,
            getTierById,
            getTierLicenseBounds,
            clampLicenseCount,
            calculatePricingTotal,
            formatPrice,
            isEnterpriseTier,
        ],
    );

    return (
        <PricingPlansContext.Provider value={value}>
            {children}
        </PricingPlansContext.Provider>
    );
}

export function usePricingPlans() {
    const context = useContext(PricingPlansContext);
    if (!context) {
        throw new Error("usePricingPlans must be used within PricingPlansProvider");
    }
    return context;
}
